import { useMemo } from 'react'

import whiteBEP from '@assets/brands/dark/bep.svg'
import whiteCC from '@assets/brands/dark/cc.svg'
import whiteDA from '@assets/brands/dark/da.svg'
import whiteItk from '@assets/brands/dark/itk.svg'
import whiteMixed from '@assets/brands/dark/mixed.svg'
import whitePrime from '@assets/brands/dark/prime.svg'
import whiteRPP from '@assets/brands/dark/rpp.svg'
import whiteSource1 from '@assets/brands/dark/source1.svg'
import whiteUnknown from '@assets/brands/dark/unknown.svg'
import colorBEP from '@assets/brands/light/bep.svg'
import colorCC from '@assets/brands/light/cc.svg'
import colorDA from '@assets/brands/light/da.svg'
import colorItk from '@assets/brands/light/itk.svg'
import colorMixed from '@assets/brands/light/mixed.svg'
import colorPrime from '@assets/brands/light/prime.svg'
import colorRPP from '@assets/brands/light/rpp.svg'
import colorSource1 from '@assets/brands/light/source1.svg'
import colorUnknown from '@assets/brands/light/unknown.svg'
import { Box } from '@mui/material'

const brandToFile = {
	'light_Buyers Edge Platform': colorBEP,
	'light_Consolidated Concepts': colorCC,
	'light_Dining Alliance': colorDA,
	light_InsideTrack: colorItk,
	'light_Prime Services': colorPrime,
	'light_Restaurant Partners Procurement': colorRPP,
	'light_Source1 Purchasing': colorSource1,
	'dark_Buyers Edge Platform': whiteBEP,
	'dark_Consolidated Concepts': whiteCC,
	'dark_Dining Alliance': whiteDA,
	dark_InsideTrack: whiteItk,
	'dark_Prime Services': whitePrime,
	'dark_Restaurant Partners Procurement': whiteRPP,
	'dark_Source1 Purchasing': whiteSource1,
}

// Extract the canonical brand names we support
const KNOWN_BRANDS = new Set<string>([
	'Buyers Edge Platform',
	'Consolidated Concepts',
	'Dining Alliance',
	'Prime Services',
	'Restaurant Partners Procurement',
	'Source1 Purchasing',
	'InsideTrack',
])

type Props = {
	brand: string
	themeMode: string // can be light or dark.
}

/**
 * Renders a BEP Brand Logo
 */
export default function BrandLogo({ brand, themeMode }: Props) {
	// Get the correct brandFile based on the brand and colorMode
	const brandFile = useMemo(() => {
		const input = (brand ?? '').trim()

		// No brand provided
		if (!input) return themeMode === 'light' ? colorUnknown : whiteUnknown

		// Check if a list of brands was provided, e.g. "Buyers Edge Platform, Prime Services"
		if (input.includes(',')) {
			const items = input
				.split(',')
				.map((s) => s.trim())
				.filter(Boolean)

			// At least 2 valid entries and all must be known to count as "mixed"
			const allKnown = items.length >= 2 && items.every((b) => KNOWN_BRANDS.has(b))
			if (allKnown) return themeMode === 'light' ? colorMixed : whiteMixed

			// List has a bad value
			return themeMode === 'light' ? colorUnknown : whiteUnknown
		}

		// Single brand -> try exact match; else unknown
		const singleKey = `${themeMode}_${input}` as keyof typeof brandToFile
		if (singleKey in brandToFile) return brandToFile[singleKey]

		// Unknown brand -> fallback to unknown
		return themeMode === 'light' ? colorUnknown : whiteUnknown
	}, [brand, themeMode])

	return (
		<Box
			component="img"
			alt="Brand Logo"
			src={brandFile}
			sx={{ height: '80', maxHeight: '1.5rem' }}
			data-testid="brand-logo"
		/>
	)
}
